import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Services from "../service/Services";

const defaultExperienceBands = [
  {
    label: "Junior (0–3 yrs)",
    short: "Junior",
    inputColor: "bg-teal-50 border-teal-200 text-teal-800",
  },
  {
    label: "Mid-level (3–6 yrs)",
    short: "Mid",
    inputColor: "bg-blue-50 border-blue-200 text-blue-800",
  },
  {
    label: "Senior (6–10 yrs)",
    short: "Senior",
    inputColor: "bg-purple-50 border-purple-200 text-purple-800",
  },
  {
    label: "Lead/Architect (10+ yrs)",
    short: "Lead",
    inputColor: "bg-amber-50 border-amber-200 text-amber-800",
  },
];

const skillCategories = [
  ".NET / .NET Core",
  "Flutter (Mobile)",
  "Angular / React (Frontend)",
  "UI/UX Designer",
  "PHP / Laravel",
  "HTML / CSS / JavaScript",
  "Business Analyst",
  "Project Manager / PMO",
  "QA Engineer / Quality Analyst",
  "AI/ML Engineer (Python)",
  "Power BI Developer",
  "Data Lake / Big Data Architect",
  "Database Administrator (DBA)",
  "DevOps Engineer",
  "Data Engineer",
  "Full Stack (.NET + Angular/React)",
  "Full Stack (Node.js + React/Angular)",
];

const getHourlyExample = (skill, bandIndex) => {
  const rates = {
    ".NET / .NET Core": [900, 1800, 2800, 4500],
    "Flutter (Mobile)": [1000, 2000, 3400, 5200],
    "Angular / React (Frontend)": [800, 1600, 2600, 4200],
    "AI/ML Engineer (Python)": [1400, 2800, 4800, 7500],
    "DevOps Engineer": [1200, 2400, 4000, 6200],
    "Full Stack (.NET + Angular/React)": [1100, 2200, 3600, 5800],
  };
  const def = [800, 1700, 3000, 5000];
  const arr = rates[skill] || def;
  if (typeof bandIndex !== "number" || bandIndex < 0 || bandIndex >= arr.length) return "";
  return arr[bandIndex];
};

const getMonthlyExample = (skill, bandIndex) => {
  const rates = {
    ".NET / .NET Core": [140000, 280000, 450000, 720000],
    "Flutter (Mobile)": [160000, 320000, 540000, 830000],
    "AI/ML Engineer (Python)": [220000, 450000, 760000, 1200000],
    "DevOps Engineer": [190000, 380000, 640000, 1000000],
    "Full Stack (.NET + Angular/React)": [170000, 350000, 580000, 930000],
  };
  const def = [130000, 270000, 480000, 800000];
  const arr = rates[skill] || def;
  if (typeof bandIndex !== "number" || bandIndex < 0 || bandIndex >= arr.length) return "";
  return arr[bandIndex];
};

export default function RateCard2026() {
  const [mode, setMode] = useState("hourly");
  const [skills, setSkills] = useState([]);
  const [currentSkill, setCurrentSkill] = useState("");

  const [experienceBands, setExperienceBands] = useState(defaultExperienceBands);

  const [hourlyInputs, setHourlyInputs] = useState(
    defaultExperienceBands.map((b, idx) => ({
      label: b.label,
      value: getHourlyExample("", idx),
    }))
  );

  const [monthlyInputs, setMonthlyInputs] = useState(
    defaultExperienceBands.map((b, idx) => ({
      label: b.label,
      value: getMonthlyExample("", idx),
    }))
  );

  const contentRef = useRef(null);
  const pdfHistoryPreviewRef = useRef(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPreview, setHistoryPreview] = useState({ open: false, row: null });
  const [historyModeFilter, setHistoryModeFilter] = useState("all");
  const [historyOpen, setHistoryOpen] = useState(true);

  const [pdfHistory, setPdfHistory] = useState([]);
  const [pdfHistoryLoading, setPdfHistoryLoading] = useState(false);
  const [pdfHistoryError, setPdfHistoryError] = useState("");
  const [pdfPreview, setPdfPreview] = useState({ open: false, row: null });
  const [pdfHistoryModeFilter, setPdfHistoryModeFilter] = useState("all");
  const [pdfHistoryOpen, setPdfHistoryOpen] = useState(true);

  const currentDate = new Date();
  const currentMonth = currentDate.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const currentYear = currentDate.getFullYear();
  const rateCardDate = `${currentMonth} ${currentYear}`;

  const isValid = (inputs) =>
    inputs.every((r) => r.value !== "" && !isNaN(r.value) && Number(r.value) >= 0);

  const handleInputChange = (setter, index, value) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index].value = value === "" ? "" : Number(value);
      return updated;
    });
  };

  const addSkill = () => {
    if (!currentSkill.trim()) return alert("Enter skill name");

    const name = currentSkill.trim();

    if (mode === "hourly") {
      if (!isValid(hourlyInputs)) return alert("Fill all hourly rates");

      setSkills((prev) => [
        ...prev,
        { name, mode: "hourly", rates: hourlyInputs.map((r) => r.value) },
      ]);

      // Persist hourly entry
      saveRateCardEntry({
        skillName: name,
        mode: "hourly",
        rateCardDate,
        rates: hourlyInputs.map((r) => ({
          bandLabel: r.label,
          value: Number(r.value),
        })),
      });

      setHourlyInputs(
        experienceBands.map((b, idx) => ({
          label: b.label,
          value: getHourlyExample("", idx),
        }))
      );
    } else {
      if (!isValid(monthlyInputs)) return alert("Fill all monthly rates");

      setSkills((prev) => [
        ...prev.filter((s) => s.mode !== "monthly" || s.name !== name),
        { name, mode: "monthly", rates: monthlyInputs.map((r) => r.value) },
      ]);

      // Persist monthly entry
      saveRateCardEntry({
        skillName: name,
        mode: "monthly",
        rateCardDate,
        rates: monthlyInputs.map((r) => ({
          bandLabel: r.label,
          value: Number(r.value),
        })),
      });

      setMonthlyInputs(
        experienceBands.map((b, idx) => ({
          label: b.label,
          value: getMonthlyExample("", idx),
        }))
      );
    }

    setCurrentSkill("");
  };

  useEffect(() => {
    if (mode === "hourly") {
      setHourlyInputs(
        experienceBands.map((b, idx) => ({
          label: b.label,
          value: getHourlyExample(currentSkill || "", idx),
        }))
      );
    } else {
      setMonthlyInputs(
        experienceBands.map((b, idx) => ({
          label: b.label,
          value: getMonthlyExample(currentSkill || "", idx),
        }))
      );
    }
  }, [currentSkill, mode, experienceBands]);

  const formatINR = (val) =>
    val == null || isNaN(val) ? "—" : `₹${Number(val).toLocaleString("en-IN")}`;

  const calcDay = (h) => (h ? Math.round(h * 8) : "—");
  const calcMonth = (h) => (h ? Math.round(h * 160) : "—");

  const downloadPDF = async () => {
    if (!contentRef.current) return;

    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2.5,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;

      let heightLeft = canvas.height;
      let position = margin;

      pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, 0);

      heightLeft -= (pdfHeight - 2 * margin) * (canvas.width / (pdfWidth - 2 * margin));

      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          position - (canvas.height - heightLeft) * (pdfWidth - 2 * margin) / canvas.width,
          pdfWidth - 2 * margin,
          0
        );
        heightLeft -= (pdfHeight - 2 * margin) * (canvas.width / (pdfWidth - 2 * margin));
      }

      pdf.save(`Rate-Card-${rateCardDate.replace(" ", "-")}-${mode}.pdf`);

      // Log PDF download snapshot (multi-skill)
      try {
        const snapshotSkills =
          mode === "hourly"
            ? skills.filter((s) => s.mode === "hourly")
            : skills.filter((s) => s.mode === "monthly");
        if (snapshotSkills.length) {
          await Services.ProposalAndRateCard.logRateCardDownload({
            mode,
            rateCardDate,
            skills: snapshotSkills,
          });
          loadPdfHistory();
        }
      } catch (e) {
        console.error("Failed to log rate card PDF download", e);
      }
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Check console.");
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      setHistoryError("");
      const res = await Services.ProposalAndRateCard.getRateCardEntries({
        limit: 100,
        ...(historyModeFilter !== "all" ? { mode: historyModeFilter } : {}),
      });
      if (res && res.success && Array.isArray(res.data)) {
        setHistory(res.data);
      } else if (Array.isArray(res)) {
        setHistory(res);
      } else {
        setHistoryError("Could not load history");
      }
    } catch (err) {
      console.error("Failed to load rate card history", err);
      setHistoryError("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadPdfHistory = async () => {
    try {
      setPdfHistoryLoading(true);
      setPdfHistoryError("");
      const res = await Services.ProposalAndRateCard.getRateCardDownloads({
        limit: 50,
        ...(pdfHistoryModeFilter !== "all" ? { mode: pdfHistoryModeFilter } : {}),
      });
      if (res && res.success && Array.isArray(res.data)) {
        setPdfHistory(res.data);
      } else if (Array.isArray(res)) {
        setPdfHistory(res);
      } else {
        setPdfHistoryError("Could not load PDF history");
      }
    } catch (err) {
      console.error("Failed to load rate card PDF history", err);
      setPdfHistoryError("Failed to load PDF history");
    } finally {
      setPdfHistoryLoading(false);
    }
  };

  const saveRateCardEntry = async (payload) => {
    try {
      await Services.ProposalAndRateCard.saveRateCardEntry(payload);
      // Refresh history in background
      loadHistory();
    } catch (err) {
      console.error("Failed to save rate card entry", err);
    }
  };

  useEffect(() => {
    loadHistory();
    loadPdfHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyModeFilter]);

  useEffect(() => {
    loadPdfHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfHistoryModeFilter]);

  const deleteHistoryRow = async (row) => {
    if (!row?._id) return;
    const ok = window.confirm("Delete this rate card history record?");
    if (!ok) return;
    try {
      await Services.ProposalAndRateCard.deleteRateCardEntry(row._id);
      if (historyPreview.open && historyPreview.row?._id === row._id) {
        setHistoryPreview({ open: false, row: null });
      }
      loadHistory();
    } catch (err) {
      console.error("Failed to delete rate card entry", err);
      alert("Delete failed.");
    }
  };

  const deletePdfHistoryRow = async (row) => {
    if (!row?._id) return;
    const ok = window.confirm("Delete this PDF history record?");
    if (!ok) return;
    try {
      await Services.ProposalAndRateCard.deleteRateCardDownload(row._id);
      if (pdfPreview.open && pdfPreview.row?._id === row._id) {
        setPdfPreview({ open: false, row: null });
      }
      loadPdfHistory();
    } catch (err) {
      console.error("Failed to delete rate card PDF history record", err);
      alert("Delete failed.");
    }
  };

  const downloadPdfFromHistory = async () => {
    if (!pdfHistoryPreviewRef.current) return;
    try {
      const canvas = await html2canvas(pdfHistoryPreviewRef.current, {
        scale: 2.5,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;

      let heightLeft = canvas.height;
      let position = margin;

      pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, 0);
      heightLeft -= (pdfHeight - 2 * margin) * (canvas.width / (pdfWidth - 2 * margin));

      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          position - (canvas.height - heightLeft) * (pdfWidth - 2 * margin) / canvas.width,
          pdfWidth - 2 * margin,
          0
        );
        heightLeft -= (pdfHeight - 2 * margin) * (canvas.width / (pdfWidth - 2 * margin));
      }

      const fileMode = pdfPreview.row?.mode || mode;
      const fileDate = (pdfPreview.row?.rateCardDate || rateCardDate || "").replace(" ", "-");
      pdf.save(`Rate-Card-${fileDate}-${fileMode}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Check console.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-4 ">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800">Rate Card</h1>
          <p className="text-gray-600 mt-2 text-lg">
            India IT Outsourcing & Contract Pricing – {currentYear}
          </p>
        </div>

        {/* Input Section – colored bands here */}
        <div className="bg-white shadow-2xl rounded-2xl p-8 border mb-12">
          <div className="flex justify-center gap-12 mb-10">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                checked={mode === "hourly"}
                onChange={() => setMode("hourly")}
                className="w-5 h-5 accent-indigo-600"
              />
              <span className="font-semibold text-lg">Hourly Mode</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                checked={mode === "monthly"}
                onChange={() => setMode("monthly")}
                className="w-5 h-5 accent-indigo-600"
              />
              <span className="font-semibold text-lg">Monthly Mode</span>
            </label>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="md:col-span-2">
              <input
                list="skills"
                value={currentSkill}
                onChange={(e) => setCurrentSkill(e.target.value)}
                placeholder="Select or type skill / role..."
                className="w-full px-5 py-4 border rounded-xl focus:ring-2 focus:ring-indigo-500 text-lg"
              />
              <datalist id="skills">
                {skillCategories.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>
            <button
              onClick={addSkill}
              disabled={!currentSkill.trim()}
              className="bg-indigo-600 text-white rounded-xl font-semibold px-8 py-4 hover:bg-indigo-700 transition disabled:opacity-50 text-lg shadow-md"
            >
              Add Skill
            </button>
          </div>

          {/* Experience band editor */}
          <div className="mb-8 border rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-lg">Experience Bands</h3>
              <button
                type="button"
                onClick={() =>
                  setExperienceBands((prev) => [
                    ...prev,
                    {
                      label: `New Band ${prev.length + 1}`,
                      short: `B${prev.length + 1}`,
                      inputColor: "bg-slate-50 border-slate-200 text-slate-800",
                    },
                  ])
                }
                className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
              >
                + Add Band
              </button>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              {experienceBands.map((b, idx) => (
                <div key={idx} className="bg-white border rounded-lg p-3 shadow-sm">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Short Label
                  </label>
                  <input
                    value={b.short}
                    onChange={(e) =>
                      setExperienceBands((prev) =>
                        prev.map((band, i) =>
                          i === idx ? { ...band, short: e.target.value } : band
                        )
                      )
                    }
                    className="w-full border rounded px-2 py-1 text-sm mb-2"
                  />
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Full Label
                  </label>
                  <input
                    value={b.label}
                    onChange={(e) =>
                      setExperienceBands((prev) =>
                        prev.map((band, i) =>
                          i === idx ? { ...band, label: e.target.value } : band
                        )
                      )
                    }
                    className="w-full border rounded px-2 py-1 text-sm mb-2"
                  />
                  {experienceBands.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setExperienceBands((prev) => prev.filter((_, i) => i !== idx))
                      }
                      className="mt-1 text-xs text-red-600 hover:text-red-700 font-semibold"
                    >
                      {/* Remove */}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(mode === "hourly" ? hourlyInputs : monthlyInputs).map((band, i) => {
              const bandInfo = experienceBands[i];
              return (
                <div
                  key={i}
                  className={`p-6 border rounded-xl transition hover:shadow-md ${bandInfo.inputColor}`}
                >
                  <div className="font-semibold mb-3 text-gray-800">{bandInfo.label}</div>
                  <input
                    type="number"
                    value={band.value ?? ""}
                    onChange={(e) =>
                      handleInputChange(
                        mode === "hourly" ? setHourlyInputs : setMonthlyInputs,
                        i,
                        e.target.value
                      )
                    }
                    className="w-full border rounded-lg px-4 py-3 focus:ring-indigo-500 bg-white"
                    placeholder="Enter rate (₹)"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Download Button */}
        {skills.length > 0 && (
          <div className="text-center mb-16">
            <button
              onClick={downloadPDF}
              className="bg-green-600 hover:bg-green-700 text-white px-12 py-5 rounded-xl font-bold shadow-xl transition transform hover:scale-105 text-xl"
            >
              Download Rate Card PDF
            </button>
          </div>
        )}

        {/* PDF CONTENT – NO COLORS here */}
        <div ref={contentRef} className="bg-white rounded-2xl shadow-xl border mb-16 overflow-hidden">
          {/* Header */}
          <div className="p-10 pb-6 text-center border-b">
            <img
              src="/header.jpg"
              alt="Company Logo"
              className="mx-auto max-w-[1100px]"
              onError={(e) => (e.target.src = "https://via.placeholder.com/380x90?text=Company+Logo")}
            />
            <h2 className="text-3xl font-bold text-red-600 mt-6">
              ZAPTAS TECHNOLOGIES - RATE CARD AS ON {rateCardDate}
            </h2>
          </div>

          {skills.length === 0 ? (
            <div className="text-center py-24 text-gray-500 text-xl">
              Add skills to generate rate card preview
            </div>
          ) : mode === "hourly" ? (
            <div className="p-10 space-y-12">
              {skills
                .filter((s) => s.mode === "hourly")
                .map((skill, i) => (
                  <div key={i} className="rounded-2xl shadow border overflow-hidden">
<div
  className="text-white px-8 py-5 font-bold"
  style={{ backgroundColor: "#14405C", fontSize: "29px" }}
>
  {skill.name} – Hourly Rates
</div>
                    <table className="w-full text-base">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-8 py-4 text-left font-semibold">Experience Band</th>
                          <th className="text-center font-semibold">Hourly</th>
                          <th className="text-center font-semibold">Daily (8h)</th>
                          <th className="text-center font-semibold">Monthly (~160h)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {experienceBands.map((band, idx) => {
                          const h = skill.rates[idx];
                          return (
                            <tr key={idx} className="border-t hover:bg-gray-50">
                              <td className="px-8 py-4 font-medium">{band.label}</td>
                              <td className="text-center">{formatINR(h)}</td>
                              <td className="text-center">{formatINR(calcDay(h))}</td>
                              <td className="text-center">{formatINR(calcMonth(h))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
            </div>
          ) : (
            <div
  className="p-5"
  style={{ paddingTop: "50px", paddingBottom: "50px" }}
>
              <div className="rounded-2xl shadow border overflow-hidden">
                <div
  className="text-white px-8 py-5   text-center transition-all duration-300"
  style={{ backgroundColor: "#14405C", fontSize: "29px", fontWeight: "bold" }}
>
  Monthly Rates – All Skills
</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-base border-collapse min-w-[900px]">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-8 py-5 text-left font-bold border-b border-r w-1/4">
                          Skill / Role
                        </th>
                        {experienceBands.map((band) => (
                          <th
                            key={band.short}
                            className="text-center font-bold border-b border-r px-6 py-5"
                          >
                            {band.short}
                            <div className="text-sm text-gray-600 font-normal mt-1">
                              {band.label}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {skills
                        .filter((s) => s.mode === "monthly")
                        .map((skill, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-8 py-5 font-medium border-r">{skill.name}</td>
                            {skill.rates.map((val, idx) => (
                              <td
                                key={idx}
                                className="text-center py-5 border-r font-medium"
                              >
                                {formatINR(val)}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="text-center py-6 text-gray-500 text-sm border-t bg-gray-50">
            <img src="/footer.jpeg" alt="Footer" className="mx-auto" />
          </div>
        </div>

        {/* Rate card history */}
        <div className="bg-white shadow-2xl rounded-2xl p-8 border mb-16">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-lg border hover:bg-gray-50"
                title={historyOpen ? "Close" : "Open"}
              >
                <span className="text-lg leading-none">{historyOpen ? "▾" : "▸"}</span>
              </button>
              <h2 className="text-2xl font-bold text-gray-800">Rate Card History</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-600">Mode</span>
              <select
                value={historyModeFilter}
                onChange={(e) => setHistoryModeFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All</option>
                <option value="hourly">Hourly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          {historyOpen && (
            <>
              {historyLoading && <p className="text-gray-500">Loading history...</p>}
              {historyError && <p className="text-red-600">{historyError}</p>}
              {!historyLoading && !historyError && history.length === 0 && (
                <p className="text-gray-500">No rate card entries recorded yet.</p>
              )}
              {history.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border-collapse">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 border">#</th>
                        <th className="px-4 py-2 border">Skill</th>
                        <th className="px-4 py-2 border">Mode</th>
                        <th className="px-4 py-2 border">Date</th>
                        <th className="px-4 py-2 border">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((row, index) => (
                        <tr key={row._id || index} className="border-t">
                          <td className="px-4 py-2 border text-center">{index + 1}</td>
                          <td className="px-4 py-2 border">{row.skillName}</td>
                          <td className="px-4 py-2 border capitalize">{row.mode}</td>
                          <td className="px-4 py-2 border">
                            {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                          </td>
                          <td className="px-4 py-2 border">
                            <div className="flex gap-2 justify-center">
                              <button
                                className="px-3 py-1 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
                                onClick={() => setHistoryPreview({ open: true, row })}
                              >
                                View
                              </button>
                              <button
                                className="px-3 py-1 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700"
                                onClick={() => deleteHistoryRow(row)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* History View Modal */}
        {historyPreview.open && (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
            onClick={() => setHistoryPreview({ open: false, row: null })}
          >
            <div
              className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div>
                  <div className="text-lg font-bold text-gray-800">
                    {historyPreview.row?.skillName || "Rate Card"}
                  </div>
                  <div className="text-sm text-gray-500">
                    Mode: <span className="capitalize">{historyPreview.row?.mode}</span> •{" "}
                    {historyPreview.row?.createdAt
                      ? new Date(historyPreview.row.createdAt).toLocaleString()
                      : "—"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg border font-semibold hover:bg-gray-50"
                    onClick={() => deleteHistoryRow(historyPreview.row)}
                  >
                    Delete
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold hover:bg-black"
                    onClick={() => setHistoryPreview({ open: false, row: null })}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="p-6">
                {Array.isArray(historyPreview.row?.rates) && historyPreview.row.rates.length > 0 ? (
                  historyPreview.row?.mode === "hourly" ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border-collapse">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 border text-left">Experience Band</th>
                            <th className="px-4 py-2 border text-right">Hourly</th>
                            <th className="px-4 py-2 border text-right">Daily (8h)</th>
                            <th className="px-4 py-2 border text-right">Monthly (~160h)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {experienceBands.map((band, idx) => {
                            const match = historyPreview.row.rates.find((r) => r.bandLabel === band.label);
                            const h = match ? Number(match.value) : "";
                            return (
                              <tr key={idx} className="border-t">
                                <td className="px-4 py-2 border">{band.label}</td>
                                <td className="px-4 py-2 border text-right">{formatINR(h)}</td>
                                <td className="px-4 py-2 border text-right">{formatINR(calcDay(h))}</td>
                                <td className="px-4 py-2 border text-right">{formatINR(calcMonth(h))}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border-collapse">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 border text-left">Experience Band</th>
                            <th className="px-4 py-2 border text-right">Monthly (INR)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {experienceBands.map((band, idx) => {
                            const match = historyPreview.row.rates.find((r) => r.bandLabel === band.label);
                            const m = match ? Number(match.value) : "";
                            return (
                              <tr key={idx} className="border-t">
                                <td className="px-4 py-2 border">{band.label}</td>
                                <td className="px-4 py-2 border text-right">{formatINR(m)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : (
                  <div className="text-gray-500">No rates data.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rate card PDF history */}
        <div className="bg-white shadow-2xl rounded-2xl p-8 border mb-16">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPdfHistoryOpen((v) => !v)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-lg border hover:bg-gray-50"
                title={pdfHistoryOpen ? "Close" : "Open"}
              >
                <span className="text-lg leading-none">{pdfHistoryOpen ? "▾" : "▸"}</span>
              </button>
              <h2 className="text-2xl font-bold text-gray-800">Rate Card PDF History</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-600">Mode</span>
              <select
                value={pdfHistoryModeFilter}
                onChange={(e) => setPdfHistoryModeFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All</option>
                <option value="hourly">Hourly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          {pdfHistoryOpen && (
            <>
              {pdfHistoryLoading && <p className="text-gray-500">Loading PDF history...</p>}
              {pdfHistoryError && <p className="text-red-600">{pdfHistoryError}</p>}
              {!pdfHistoryLoading && !pdfHistoryError && pdfHistory.length === 0 && (
                <p className="text-gray-500">No PDFs downloaded yet.</p>
              )}
              {pdfHistory.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border-collapse">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 border">#</th>
                        <th className="px-4 py-2 border">Mode</th>
                        <th className="px-4 py-2 border">Rate Card Date</th>
                        <th className="px-4 py-2 border">Skills</th>
                        <th className="px-4 py-2 border">Downloaded At</th>
                        <th className="px-4 py-2 border">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pdfHistory.map((row, index) => (
                        <tr key={row._id || index} className="border-t">
                          <td className="px-4 py-2 border text-center">{index + 1}</td>
                          <td className="px-4 py-2 border capitalize text-center">{row.mode}</td>
                          <td className="px-4 py-2 border text-center">{row.rateCardDate || "—"}</td>
                          <td className="px-4 py-2 border">
                            {Array.isArray(row.skills) ? row.skills.length : 0}
                          </td>
                          <td className="px-4 py-2 border text-center">
                            {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                          </td>
                          <td className="px-4 py-2 border">
                            <div className="flex gap-2 justify-center">
                              <button
                                className="px-3 py-1 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
                                onClick={() => setPdfPreview({ open: true, row })}
                              >
                                View
                              </button>
                              <button
                                className="px-3 py-1 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700"
                                onClick={() => deletePdfHistoryRow(row)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* PDF History View Modal */}
        {pdfPreview.open && (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
            onClick={() => setPdfPreview({ open: false, row: null })}
          >
            <div
              className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div>
                  <div className="text-lg font-bold text-gray-800">Rate Card PDF Preview</div>
                  <div className="text-sm text-gray-500">
                    Mode: <span className="capitalize">{pdfPreview.row?.mode}</span> •{" "}
                    {pdfPreview.row?.rateCardDate || "—"} •{" "}
                    {pdfPreview.row?.createdAt
                      ? new Date(pdfPreview.row.createdAt).toLocaleString()
                      : "—"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700"
                    onClick={downloadPdfFromHistory}
                  >
                    Download PDF
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg border font-semibold hover:bg-gray-50"
                    onClick={() => deletePdfHistoryRow(pdfPreview.row)}
                  >
                    Delete
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold hover:bg-black"
                    onClick={() => setPdfPreview({ open: false, row: null })}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="p-6 bg-gray-50 overflow-auto" style={{ maxHeight: "75vh" }}>
                <div ref={pdfHistoryPreviewRef} className="bg-white rounded-2xl shadow-xl border overflow-hidden">
                  {/* Header */}
                  <div className="p-10 pb-6 text-center border-b">
                    <img
                      src="/header.jpg"
                      alt="Company Logo"
                      className="mx-auto max-w-[1100px]"
                    />
                    <h2 className="text-3xl font-bold text-red-600 mt-6">
                      ZAPTAS TECHNOLOGIES - RATE CARD AS ON {pdfPreview.row?.rateCardDate || rateCardDate}
                    </h2>
                  </div>

                  {/* Body */}
                  {pdfPreview.row?.mode === "hourly" ? (
                    <div className="p-10 space-y-12">
                      {(Array.isArray(pdfPreview.row?.skills) ? pdfPreview.row.skills : []).map((skill, i) => (
                        <div key={i} className="rounded-2xl shadow border overflow-hidden">
                          <div
                            className="text-white px-8 py-5 font-bold"
                            style={{ backgroundColor: "#14405C", fontSize: "29px" }}
                          >
                            {skill.name} – Hourly Rates
                          </div>
                          <table className="w-full text-base">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-8 py-4 text-left font-semibold">Experience Band</th>
                                <th className="text-center font-semibold">Hourly</th>
                                <th className="text-center font-semibold">Daily (8h)</th>
                                <th className="text-center font-semibold">Monthly (~160h)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {experienceBands.map((band, idx) => {
                                const h = skill.rates?.[idx];
                                return (
                                  <tr key={idx} className="border-t hover:bg-gray-50">
                                    <td className="px-8 py-4 font-medium">{band.label}</td>
                                    <td className="text-center">{formatINR(h)}</td>
                                    <td className="text-center">{formatINR(calcDay(h))}</td>
                                    <td className="text-center">{formatINR(calcMonth(h))}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-5" style={{ paddingTop: "50px", paddingBottom: "50px" }}>
                      <div className="rounded-2xl shadow border overflow-hidden">
                        <div
                          className="text-white px-8 py-5 text-center transition-all duration-300"
                          style={{ backgroundColor: "#14405C", fontSize: "29px", fontWeight: "bold" }}
                        >
                          Monthly Rates – All Skills
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-base border-collapse min-w-[900px]">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-8 py-5 text-left font-bold border-b border-r w-1/4">
                                  Skill / Role
                                </th>
                                {experienceBands.map((band) => (
                                  <th
                                    key={band.short}
                                    className="text-center font-bold border-b border-r px-6 py-5"
                                  >
                                    {band.short}
                                    <div className="text-sm text-gray-600 font-normal mt-1">
                                      {band.label}
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(Array.isArray(pdfPreview.row?.skills) ? pdfPreview.row.skills : []).map((skill, i) => (
                                <tr key={i} className="border-b hover:bg-gray-50">
                                  <td className="px-8 py-5 font-medium border-r">{skill.name}</td>
                                  {(skill.rates || []).map((val, idx) => (
                                    <td key={idx} className="text-center py-5 border-r font-medium">
                                      {formatINR(val)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-center py-6 text-gray-500 text-sm border-t bg-gray-50">
                    <img src="/footer.jpeg" alt="Footer" className="mx-auto" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}